<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { string } from 'yup'
import { emailResetPasswordUpdateAction } from '@/api/functions/auth/email-reset-password-update.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { passwordErr, passwordRegex, requiredErr } from '@/constants'
import { getErrorMessage } from '@/utils/errorUtils'
import { toEnglishDigits } from '@/utils/to-english-number.util'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const token = route.query.token as string
const email = route.query.email as string

const currentStep = ref('new-password')
const loading = ref(false)
const showPassword = ref(false)

const step = computed(() => currentStep.value)

const validationSchema = {
  password: string()
    .transform((value: string) => toEnglishDigits(value))
    .matches(passwordRegex, passwordErr)
    .required(requiredErr),
}

const initialValues = {
  password: '',
}

const { mutate: resetPasswordMutate } = useMutationApi({
  mutationKey: ['resetPasswordUpdate'],
  mutationFn: (payload: { password: string }) =>
    emailResetPasswordUpdateAction({ email, token, password: payload.password }),
  onSuccess: (res) => {
    toast.success('رمز عبور با موفقیت تغییر کرد.')
    currentStep.value = 'confirmation'
    loading.value = false
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err), 'خطا')
    loading.value = false
  },
})

const resetPassword = async (values: { password: string }) => {
  loading.value = true
  await resetPasswordMutate({ password: values.password })
}

const goToLogin = () => {
  router.push('/auth/login')
}
</script>

<template>
  <div class="flex items-center justify-center mb-12">
    <div>
      <div class="w-[352px] bg-white rounded-2xl p-8">
        <!-- Step 1: New Password -->
        <div v-if="step === 'new-password'">
          <!-- Title -->
          <div class="text-lg font-bold text-[#1b325a] text-start pb-4">بازیابی رمز عبور</div>

          <!-- Description -->
          <div class="text-base text-[#1b325a] text-right mb-[22px]">
            رمز عبور جدید برای حساب کاربری خود انتخاب کنید.
          </div>

          <!-- Reset Form -->
          <Form
            :validation-schema="validationSchema"
            :initial-values="initialValues"
            @submit="resetPassword"
            class="space-y-4"
          >
            <!-- Password Field -->
            <div class="relative">
              <Input
                name="password"
                label="رمز عبور جدید"
                :type="showPassword ? 'text' : 'password'"
              />
            </div>

            <!-- Submit Button -->
            <Button
              :loading="loading"
              type="submit"
              :disabled="loading"
              class="mt-6 w-full rounded-lg text-[#0f1114] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>تغییر رمز عبور</span>
            </Button>
          </Form>
        </div>

        <!-- Step 2: Confirmation -->
        <div v-else class="text-center">
          <!-- Success Icon -->
          <div class="flex justify-center mb-4">
            <img src="/svg/ic-tick-circle.0cdc84c.svg" width="64" height="64" />
          </div>

          <!-- Success Message -->
          <div class="text-base text-[#1b325a] text-center mb-6">
            رمز عبور شما با موفقیت تغییر کرد. اکنون می‌توانید با ایمیل و رمز عبور جدید خود وارد حساب
            کاربری شوید.
          </div>

          <!-- Success Button -->
          <Button @click="goToLogin" class="w-full rounded-lg text-[#0f1114] font-medium">
            <span>بازگشت به صفحه ورود</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
